import React, { useContext, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { ScreenWidth } from "src/stores/UIStore";
import { observer } from "mobx-react";
import { UI } from "@wwimmo/ui";
import { TicketForm } from "./form/TicketForm";
import styles from "./TicketDetails.module.css";
import { LOADING_TYPE, TICKET_STATE } from "src/stores/TicketStore";
import { Error } from "src/screens/error/Error";
import { Route, getParams, selectRoute } from "src/config/routes";
import { TicketActionCard } from "./cards/TicketActionCard";
import { TicketContactCard } from "./cards/TicketContactCard";
import { TicketWorkflowCard } from "./cards/TicketWorkflowCard";
import { OrderLinkListCard } from "./cards/OrderLinkListCard";
import { TicketHistory } from "./history/TicketHistory";

const TicketDetailsBase = () => {
    const { t } = useTranslation();
    const { navStore, ticketStore, uiStore, uiSidebarStore, authStore } = useContext(RootStoreContext);
    const ticketNumber = getParams(Route.ticketDetails, authStore.user?.role)["ticketnumber"];

    useEffect(() => {
        navStore.setTitle("");
        navStore.setActionButtonMiddle(null);
        navStore.setActionButtonRight(null);

        navStore.setBackbutton(
            selectRoute(Route.tickets, authStore.user?.role),
            t("screens.tickets.sidebar_title").toString()
        );
    }, [navStore, authStore, t, ticketStore]);

    // Initialize Data for Ticket
    useEffect(() => {
        async function initializeTicketData() {
            await ticketStore.init(ticketNumber);
        }
        initializeTicketData();
    }, [ticketStore, ticketNumber]);

    if (ticketStore.error) {
        return <Error message={ticketStore.error.title} />;
    }

    if (!ticketStore.currentTicket) {
        return null;
    }

    return (
        <UI.Container
            className={`pt-3 pt-md-5 ${styles.TicketDetailsContainer} ${
                !uiStore.isMobile && uiSidebarStore.isSidebarCollapsed ? styles.ExtraPadding : ""
            }`}
            fluid
        >
            <TicketDetailsTitle />
            <UI.Row>
                <UI.Col sm={12} lg={9}>
                    <TicketDetailsFormAndHistory />
                </UI.Col>

                <UI.Col sm={12} lg={3}>
                    <TicketDetailsWorkflowAndActionCard />
                    <TicketDetailsOrderLinkListCard />
                    <TicketDetailsContactCard />
                </UI.Col>
            </UI.Row>
        </UI.Container>
    );
};

const TicketDetailsTitleBase = () => {
    const { t } = useTranslation();
    const { ticketStore } = useContext(RootStoreContext);

    return (
        <div className="px-2 px-md-0">
            <UI.Title>
                {ticketStore.currentTicket?.isNewTicket ? t("screens.tickets.new_ticket") : t("screens.tickets.ticket")}
            </UI.Title>
        </div>
    );
};

const TicketDetailsFormAndHistoryBase = () => {
    const { ticketStore, uiStore } = useContext(RootStoreContext);

    if (ticketStore.loadingType === LOADING_TYPE.TICKET) {
        return <UI.RotatingSpinner noLogo size={50} className={styles.LoadingTicketSpinner} />;
    }

    if (uiStore.isMobile) {
        return <TicketForm />;
    }

    return (
        <React.Fragment>
            <TicketForm />
            <TicketHistory />
        </React.Fragment>
    );
};

const TicketDetailsWorkflowAndActionCardBase = () => {
    const { ticketStore } = useContext(RootStoreContext);

    if (ticketStore.currentTicket?.isNewTicket) {
        return null;
    }

    const withSpacer = window.innerWidth <= ScreenWidth.MD;
    const isTicketNotArchived = ticketStore.currentTicket?.state !== TICKET_STATE.ARCHIVED;

    return (
        <React.Fragment>
            {withSpacer ? <UI.Spacer /> : null}
            <TicketWorkflowCard />
            {isTicketNotArchived ? <TicketActionCard /> : null}
        </React.Fragment>
    );
};

const TicketDetailsOrderLinkListCardBase = () => {
    const { ticketStore } = useContext(RootStoreContext);

    if (ticketStore.currentTicket?.isNewTicket || !ticketStore.currentTicket?.orders) {
        return null;
    }

    return (
        <OrderLinkListCard
            orders={ticketStore.currentTicket.orders}
            ticketNumber={ticketStore.currentTicket.number ? ticketStore.currentTicket.number.toString() : ""}
        />
    );
};

const TicketDetailsContactCardBase = () => {
    const { ticketStore } = useContext(RootStoreContext);

    if (!ticketStore.currentTicket?.incidentPersons || ticketStore.currentTicket.incidentPersons.length === 0) {
        return null;
    }
    return <TicketContactCard incidentPersons={ticketStore.currentTicket.incidentPersons} />;
};

const TicketDetailsTitle = observer(TicketDetailsTitleBase);
const TicketDetailsFormAndHistory = observer(TicketDetailsFormAndHistoryBase);
const TicketDetailsWorkflowAndActionCard = observer(TicketDetailsWorkflowAndActionCardBase);
const TicketDetailsOrderLinkListCard = observer(TicketDetailsOrderLinkListCardBase);
const TicketDetailsContactCard = observer(TicketDetailsContactCardBase);

export const TicketDetails = Sentry.withProfiler(observer(TicketDetailsBase));
